import React, { useState } from 'react';
import { Check, RotateCcw, User, Mail, Phone, MapPin, Briefcase, Building } from 'lucide-react';
import { cn } from '../lib/utils';

const EMOJIS = ["👤", "🛡️", "📊", "🚀", "💼", "🎯", "⚡", "🌟", "🧠", "🪄", "🦊", "🐼"];

export function ProfilePage({ user }) {
  const displayUser = user || { name: "Admin User", email: "admin@image-i.com", role: "admin" };

  const [form, setForm] = useState({
    name: displayUser.name || "",
    title: "Head of Product Design",
    department: "Product",
    phone: "",
    location: "",
    bio: "",
    avatarEmoji: "👤",
  });
  const [saved, setSaved] = useState(false);

  const onSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="pb-12">
      {/* Page header */}
      <div className="px-6 lg:px-10 py-6 border-b border-border">
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Account</p>
        <h1 className="text-2xl font-semibold text-foreground">Profile &amp; Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Update your personal details and preferences.</p>
      </div>

      <div className="px-6 lg:px-10 grid lg:grid-cols-3 gap-6 mt-6">
        {/* Identity card */}
        <div className="surface-card rounded-xl p-6 lg:col-span-1 flex flex-col items-center text-center gap-4">
          <div
            className="h-24 w-24 rounded-2xl grid place-items-center text-4xl shadow-lg"
            style={{ background: 'var(--gradient-primary)' }}
          >
            <span>{form.avatarEmoji}</span>
          </div>
          <div>
            <div className="font-semibold text-lg text-foreground">{form.name || "Unnamed user"}</div>
            <div className="text-sm text-muted-foreground">{displayUser.email}</div>
            <span className="inline-block mt-2 px-3 py-0.5 rounded-full border border-border text-xs capitalize text-muted-foreground">
              {displayUser.role}
            </span>
          </div>

          <div className="w-full border-t border-border pt-4">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-3">Avatar</p>
            <div className="grid grid-cols-6 gap-2">
              {EMOJIS.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, avatarEmoji: e }))}
                  className={cn(
                    "h-9 w-9 rounded-lg border text-lg grid place-items-center hover:bg-muted transition",
                    form.avatarEmoji === e ? "border-primary ring-2 ring-primary/30 bg-primary/5" : "border-border"
                  )}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          <div className="w-full border-t border-border pt-4 text-left">
            <dl className="text-xs space-y-2">
              <div className="flex justify-between">
                <dt className="text-muted-foreground flex items-center gap-1.5"><Briefcase className="w-3 h-3" /> Title</dt>
                <dd className="font-medium">{form.title || "—"}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground flex items-center gap-1.5"><Building className="w-3 h-3" /> Department</dt>
                <dd className="font-medium">{form.department || "—"}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Location</dt>
                <dd className="font-medium">{form.location || "—"}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground flex items-center gap-1.5"><Phone className="w-3 h-3" /> Phone</dt>
                <dd className="font-medium">{form.phone || "—"}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Details form */}
        <div className="surface-card rounded-xl p-6 lg:col-span-2">
          <h2 className="font-semibold text-foreground mb-1">Personal details</h2>
          <p className="text-sm text-muted-foreground mb-5">How you appear across IMAGE-I dashboards and reports.</p>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label htmlFor="name" className="text-sm font-medium text-foreground">Full name</label>
              <input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-input text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="title" className="text-sm font-medium text-foreground">Title</label>
              <input
                id="title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-input text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="department" className="text-sm font-medium text-foreground">Department</label>
              <input
                id="department"
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-input text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="location" className="text-sm font-medium text-foreground">Location</label>
              <input
                id="location"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-input text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="phone" className="text-sm font-medium text-foreground">Phone</label>
              <input
                id="phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-input text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-sm font-medium text-foreground">Email</label>
              <input
                id="email"
                value={displayUser.email}
                disabled
                className="w-full px-3 py-2 rounded-lg border border-border bg-muted text-sm text-muted-foreground outline-none cursor-not-allowed"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label htmlFor="bio" className="text-sm font-medium text-foreground">Bio</label>
              <textarea
                id="bio"
                rows={3}
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-input text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/40 resize-none"
              />
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <button
                onClick={onSave}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition",
                  saved
                    ? "bg-success text-white"
                    : "bg-primary text-white hover:opacity-90"
                )}
              >
                <Check className="h-4 w-4" />
                {saved ? "Saved!" : "Save changes"}
              </button>
            </div>
          </div>
        </div>

        {/* Appearance card */}
        <div className="surface-card rounded-xl p-6 lg:col-span-3">
          <h2 className="font-semibold text-foreground mb-1">Appearance</h2>
          <p className="text-sm text-muted-foreground mb-5">Theme and display preferences are applied globally.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { label: "Theme", description: "Light / Dark toggle available in the top bar" },
              { label: "Language", description: "English (US)" },
              { label: "Timezone", description: "UTC+05:30 India Standard Time" },
            ].map((item) => (
              <div key={item.label} className="rounded-lg border border-border p-4">
                <div className="text-sm font-medium text-foreground">{item.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{item.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
